import {
  OrderItemsGetRequestSchema,
  OrderItemsStatusSummaryRequestSchema,
  type OrderItemsSummaryRequestSchema,
  orderItemsContract,
} from '@mntn/api-contracts/flight-deck/v1/order-items'
import type { ClientInferRequest, ClientInferResponseBody, ClientInferResponses } from '@ts-rest/core'
import type { z } from 'zod'
import { useFlightDeckTsRestClient } from '@/flight-deck-api/ts-rest'
import { useAuthenticatedSession } from '@/providers/session'

export type AddBudgetResponse = ClientInferResponses<typeof orderItemsContract.addBudget>

export type AddBudgetBody = ClientInferRequest<typeof orderItemsContract.addBudget>['body']
type CreateCampaignRequest = ClientInferRequest<typeof orderItemsContract.createCampaign>
type CreateCampaignExperimentRequest = ClientInferRequest<typeof orderItemsContract.createCampaignExperiment>
export type CreateCampaignInput = CreateCampaignRequest['body']
export type CreateCampaignExperimentInput = CreateCampaignExperimentRequest['body']

export type OrderItemsGet = Omit<z.input<typeof OrderItemsGetRequestSchema>, 'advertiser-id'>

type OrderItemsStatusSummaryGet = z.input<typeof OrderItemsStatusSummaryRequestSchema>

type OrderItemsSummaryGet = Omit<z.input<typeof OrderItemsSummaryRequestSchema>, 'advertiser-id'>

export type OrderItemsOrder = OrderItemsGet['order']
export type OrderItemsOrderBy = OrderItemsGet['order-by']
export type OrderItemsStatusFilter = OrderItemsGet['status']

export type OrderItem = ClientInferResponseBody<typeof orderItemsContract.list, 200>['data'][0]

export const useOrderItemsService = () => {
  const orderItemsClient = useFlightDeckTsRestClient(orderItemsContract)
  const { session } = useAuthenticatedSession()

  return {
    getOrderItems: {
      handler: async (query: OrderItemsGet = {}) => {
        const response = await orderItemsClient.list({
          query: { ...query, 'advertiser-id': session.advertiser.id },
        })
        if (response.status !== 200) {
          throw new Error('There was an issue fetching the order items. Try again.')
        }
        return response.body
      },
      schema: OrderItemsGetRequestSchema,
    },

    getOrderItemByCampaignGroupId: {
      handler: async ({ campaignGroupId }: { campaignGroupId: number }) => {
        const response = await orderItemsClient.list({
          query: {
            'advertiser-id': session.advertiser.id,
            'campaign-group-id': [campaignGroupId],
          },
        })
        if (response.status === 200) {
          return response.body.data[0]
        }
        throw new Error('Failed to fetch order item')
      },
    },

    getOrderItemsStatusSummary: {
      handler: async (query: OrderItemsStatusSummaryGet) => {
        const response = await orderItemsClient.statusSummary({ query })
        if (response.status !== 200) {
          throw new Error('There was an issue fetching the order items status summary. Try again.')
        }
        return response.body.data
      },
      schema: OrderItemsStatusSummaryRequestSchema,
    },

    getOrderItemSummary: {
      handler: async (query: OrderItemsSummaryGet = {}) => {
        const response = await orderItemsClient.summary({
          query: { ...query, 'advertiser-id': session.advertiser.id },
        })
        if (response.status !== 200) {
          throw new Error('There was an issue fetching the order item summary. Try again.')
        }
        return response.body.data
      },
    },

    createCampaign: {
      handler: async (request: CreateCampaignRequest) => {
        const response = await orderItemsClient.createCampaign(request)
        if (response.status !== 200) {
          throw new Error('There was an issue creating your campaign. Try again.')
        }
        return response.body.data
      },
    },

    createCampaignExperiment: {
      handler: async (request: CreateCampaignExperimentRequest) => {
        const response = await orderItemsClient.createCampaignExperiment(request)
        if (response.status !== 200) {
          throw new Error('There was an issue creating your campaign experiment. Try again.')
        }
        return response.body.data
      },
    },

    addBudget: {
      handler: async ({ orderItemId, body }: { orderItemId: number; body: AddBudgetBody }) => {
        const response = await orderItemsClient.addBudget({
          params: { 'order-item-id': orderItemId },
          body,
        })
        return response
      },
    },
  }
}
